import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Elements } from '@stripe/react-stripe-js'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { useCurrency } from '../contexts/CurrencyContext'
import { paymentService } from '../services/paymentService'
import { stripePlans, stripePromise } from '../lib/stripe'
import CheckoutForm from '../components/CheckoutForm'
import CheckoutCurrencySelector from '../components/CheckoutCurrencySelector'
import {
  ArrowLeftIcon,
  CheckIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  CameraIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface BillingInfo {
  email: string
  name: string
  company: string
  address: string
  city: string
  state: string
  zipCode: string
  country: string
}

const CheckoutPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refreshUser } = useAuth()
  const { showSuccess, showError } = useNotification()
  const { selectedCurrency, formatPrice, convertPrice } = useCurrency()
  
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'professional')
  const [loading, setLoading] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<Partial<BillingInfo>>({})
  const [billingInfo, setBillingInfo] = useState<BillingInfo>({
    email: user?.email || '',
    name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    company: user?.businessName || '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  })

  const plan = stripePlans[selectedPlan as keyof typeof stripePlans]

  useEffect(() => {
    if (!user) {
      navigate('/auth')
      return
    }

    // If user is already on a paid plan, redirect to upgrade page
    if (user.subscriptionStatus && user.subscriptionStatus !== 'freemium') {
      navigate('/upgrade')
      return
    }
  }, [user, navigate])

  // Validate billing information
  const validateBillingInfo = (): boolean => {
    const errors: Partial<BillingInfo> = {}
    
    if (!billingInfo.email) errors.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(billingInfo.email)) errors.email = 'Invalid email format'
    
    if (!billingInfo.name) errors.name = 'Full name is required'
    if (!billingInfo.address) errors.address = 'Address is required'
    if (!billingInfo.city) errors.city = 'City is required'
    if (!billingInfo.state) errors.state = 'State is required'
    if (!billingInfo.zipCode) errors.zipCode = 'ZIP code is required'
    
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handlePlanChange = (planId: string) => {
    setSelectedPlan(planId)
    // Update URL without page reload
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('plan', planId)
    navigate(`/checkout?${newSearchParams.toString()}`, { replace: true })
  }

  const handleBillingInfoChange = (field: keyof BillingInfo, value: string) => {
    setBillingInfo(prev => ({ ...prev, [field]: value }))
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const initializePayment = async () => {
    if (!user || !plan || plan.id === 'freemium') return
    if (!validateBillingInfo()) return

    setLoading(true)
    try {
      // Convert price to selected currency (already in cents)
      const convertedPrice = convertPrice(plan.price)
      
      const result = await paymentService.createPaymentIntent({
        planId: selectedPlan,
        userId: user.id,
        billingInfo: {
          ...billingInfo,
          currency: selectedCurrency.code
        },
        amount: convertedPrice,
        currency: selectedCurrency.code
      })

      if (result.success && result.clientSecret) {
        setClientSecret(result.clientSecret)
      } else {
        showError('Payment Setup Error', result.error || 'Failed to initialize payment')
      }
    } catch (error) {
      console.error('Payment initialization error:', error)
      showError('Payment Setup Error', 'An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      const verifyResult = await paymentService.verifyPaymentAndUpdateSubscription(
        paymentIntentId,
        user!.id,
        selectedPlan
      )

      if (verifyResult.success) {
        await refreshUser()
        showSuccess('Subscription Active', `Welcome to ${plan.name}! Your subscription is now active.`)
        navigate('/checkout/success?plan=' + selectedPlan)
      } else {
        showError('Payment Error', verifyResult.error || 'Failed to activate subscription')
      }
    } catch (error) {
      console.error('Payment verification error:', error)
      showError('Payment Error', 'Payment processed but subscription activation failed. Please contact support.')
    }
  }

  if (!user) {
    return null
  }

  const appearance = {
    theme: 'night' as const,
    variables: {
      colorPrimary: '#3b82f6',
      colorBackground: 'rgba(255, 255, 255, 0.05)',
      colorText: '#ffffff',
      colorDanger: '#ef4444',
      fontFamily: 'Inter, system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => navigate('/upgrade')}
              className="flex items-center space-x-3 text-white hover:text-gray-300 transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
              <span className="font-medium">Back to Plans</span>
            </motion.button>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 glass-badge rounded-2xl flex items-center justify-center">
                <CameraIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">
                Shoot<span className="text-gray-400">Suite</span>
              </span>
            </motion.div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Plan Selection & Details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Plan Selection */}
            <div className="glass-mockup rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-6">Select Your Plan</h2>
              
              <div className="space-y-4">
                {Object.values(stripePlans).filter(p => p.id !== 'freemium').map((planOption) => {
                  const isSelected = selectedPlan === planOption.id
                  const Icon = planOption.id === 'professional' ? CameraIcon : BuildingOfficeIcon
                  
                  return (
                    <motion.button
                      key={planOption.id}
                      onClick={() => handlePlanChange(planOption.id)}
                      className={`w-full p-5 rounded-xl border-2 transition-all text-left relative overflow-hidden ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                          : 'border-white/20 bg-white/5 hover:border-white/30 hover:bg-white/10'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {/* Popular badge for Professional plan */}
                      {planOption.id === 'professional' && (
                        <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                          POPULAR
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            planOption.id === 'professional' 
                              ? 'bg-gradient-to-r from-blue-500 to-purple-600'
                              : 'bg-gradient-to-r from-purple-500 to-pink-600'
                          }`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-white text-lg">{planOption.name}</h3>
                            <p className="text-gray-400">
                              <span className="text-2xl font-bold text-white">
                                {formatPrice(planOption.price)}
                              </span>
                              <span className="text-sm">/{planOption.interval}</span>
                            </p>
                          </div>
                        </div>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center"
                          >
                            <CheckIcon className="w-5 h-5 text-white" />
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  )
                })}
              </div>
            </div>

            {/* Plan Features */}
            <div className="glass-mockup rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">
                {plan.name} Plan Features
              </h3>
              
              <div className="space-y-4">
                {(plan.id === 'professional' ? [
                  'Unlimited client projects',
                  'Advanced shoot management',
                  'AI-powered task automation',
                  'Priority email support',
                  'Unlimited invoicing & payments',
                  'Advanced client portals',
                  'File storage (100GB)',
                  'Custom branding'
                ] : [
                  'Everything in Professional',
                  'Team collaboration tools',
                  'Multi-user access (up to 10)',
                  'Advanced project templates',
                  'White-label client portals',
                  'File storage (1TB)',
                  'Advanced analytics dashboard',
                  'API access & integrations'
                ]).map((feature, index) => (
                  <motion.div 
                    key={index} 
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start space-x-3 py-2"
                  >
                    <CheckIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 leading-relaxed">{feature}</span>
                  </motion.div>
                ))}
              </div>
              
              {/* Additional plan benefits */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-400" />
                  <span>30-day money-back guarantee</span>
                </div>
              </div>
            </div>

            {/* Security Badges */}
            <div className="space-y-3">
              <div className="glass-mockup rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  <ShieldCheckIcon className="w-6 h-6 text-emerald-400" />
                  <div>
                    <p className="text-white font-medium">Secure Payment</p>
                    <p className="text-gray-400 text-sm">256-bit SSL encryption</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-mockup rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  <LockClosedIcon className="w-6 h-6 text-blue-400" />
                  <div>
                    <p className="text-white font-medium">PCI Compliant</p>
                    <p className="text-gray-400 text-sm">Your card details are never stored</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Billing & Payment */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Order Summary */}
            <div className="glass-mockup rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Order Summary</h2>
              
              <div className="space-y-4">
                {/* Currency Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Currency
                  </label>
                  <CheckoutCurrencySelector />
                </div>
                
                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">{plan.name} Plan</span>
                    <span className="text-white font-semibold">
                      {formatPrice(plan.price)}
                    </span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Billing cycle</span>
                  <span className="text-gray-300">Monthly</span>
                </div>
                
                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Total</span>
                    <span className="text-white font-bold text-xl">
                      {formatPrice(plan.price)}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    Billed monthly • Cancel anytime
                  </p>
                </div>
              </div>
            </div>

            {/* Billing Information */}
            <div className="glass-mockup rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Billing Information</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={billingInfo.email}
                      onChange={(e) => handleBillingInfoChange('email', e.target.value)}
                      className={`input-field ${validationErrors.email ? 'border-red-500' : ''}`}
                      placeholder="your@email.com"
                    />
                    {validationErrors.email && (
                      <p className="text-red-400 text-sm mt-1 flex items-center">
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        {validationErrors.email}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={billingInfo.name}
                      onChange={(e) => handleBillingInfoChange('name', e.target.value)}
                      className={`input-field ${validationErrors.name ? 'border-red-500' : ''}`}
                      placeholder="John Doe"
                    />
                    {validationErrors.name && (
                      <p className="text-red-400 text-sm mt-1 flex items-center">
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        {validationErrors.name}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Company (Optional)
                  </label>
                  <input
                    type="text"
                    value={billingInfo.company}
                    onChange={(e) => handleBillingInfoChange('company', e.target.value)}
                    className="input-field"
                    placeholder="Your Company Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Address *
                  </label>
                  <input
                    type="text"
                    value={billingInfo.address}
                    onChange={(e) => handleBillingInfoChange('address', e.target.value)}
                    className={`input-field ${validationErrors.address ? 'border-red-500' : ''}`}
                    placeholder="123 Main Street"
                  />
                  {validationErrors.address && (
                    <p className="text-red-400 text-sm mt-1 flex items-center">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                      {validationErrors.address}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      value={billingInfo.city}
                      onChange={(e) => handleBillingInfoChange('city', e.target.value)}
                      className={`input-field ${validationErrors.city ? 'border-red-500' : ''}`}
                      placeholder="New York"
                    />
                    {validationErrors.city && (
                      <p className="text-red-400 text-sm mt-1 flex items-center">
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        {validationErrors.city}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      value={billingInfo.state}
                      onChange={(e) => handleBillingInfoChange('state', e.target.value)}
                      className={`input-field ${validationErrors.state ? 'border-red-500' : ''}`}
                      placeholder="NY"
                    />
                    {validationErrors.state && (
                      <p className="text-red-400 text-sm mt-1 flex items-center">
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        {validationErrors.state}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      value={billingInfo.zipCode}
                      onChange={(e) => handleBillingInfoChange('zipCode', e.target.value)}
                      className={`input-field ${validationErrors.zipCode ? 'border-red-500' : ''}`}
                      placeholder="10001"
                    />
                    {validationErrors.zipCode && (
                      <p className="text-red-400 text-sm mt-1 flex items-center">
                        <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                        {validationErrors.zipCode}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            {!clientSecret ? (
              <div className="glass-mockup rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Payment Method</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 rounded-xl bg-white/5 border border-white/20">
                    <CreditCardIcon className="w-6 h-6 text-blue-400" />
                    <div>
                      <p className="text-white font-medium">Credit or Debit Card</p>
                      <p className="text-gray-400 text-sm">Visa, Mastercard, American Express</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 text-sm text-gray-400">
                    <LockClosedIcon className="w-4 h-4" />
                    <span>Your payment information is encrypted and secure</span>
                  </div>
                </div>

                {/* Initialize Payment Button */}
                <motion.button
                  onClick={initializePayment}
                  disabled={loading}
                  className={`w-full mt-6 py-4 px-6 rounded-xl font-semibold text-lg transition-all ${
                    loading
                      ? 'bg-gray-500/20 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
                  }`}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Setting up payment...</span>
                    </div>
                  ) : (
                    'Continue to Payment'
                  )}
                </motion.button>
              </div>
            ) : (
              <div className="glass-mockup rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Complete Payment</h3>
                
                <Elements 
                  stripe={stripePromise} 
                  options={{ 
                    clientSecret,
                    appearance 
                  }}
                >
                  <CheckoutForm 
                    onSuccess={handlePaymentSuccess}
                    planName={plan.name}
                    amount={convertPrice(plan.price)}
                  />
                </Elements>
              </div>
            )}

            <p className="text-gray-400 text-sm text-center">
              By subscribing, you agree to our Terms of Service and Privacy Policy. 
              You can cancel your subscription at any time.
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  )
}

export default CheckoutPage 